import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { clientApi, CLIENT_TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(CLIENT_TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await clientApi.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem(CLIENT_TOKEN_KEY);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth callback, let AuthCallback exchange the session_id first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await clientApi.post("/auth/login", { email, password });
    if (data.password_setup_required || data.two_factor_required) return data;
    localStorage.setItem(CLIENT_TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data;
  };

  const completeSetup = async (setupToken, newPassword) => {
    const { data } = await clientApi.post("/auth/set-password", {
      setup_token: setupToken, new_password: newPassword,
    });
    if (data.two_factor_required) return data;
    localStorage.setItem(CLIENT_TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  };

  const verifyTwoFactor = async (challengeId, code) => {
    const { data } = await clientApi.post("/auth/2fa/verify", { challenge_id: challengeId, code });
    localStorage.setItem(CLIENT_TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  };

  const resendTwoFactor = async (challengeId) => {
    const { data } = await clientApi.post("/auth/2fa/resend", { challenge_id: challengeId });
    return data;
  };

  const googleComplete = useCallback(async (sessionId) => {
    const { data } = await clientApi.post("/auth/google/session", { session_id: sessionId });
    localStorage.setItem(CLIENT_TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const signup = async (payload) => {
    const { data } = await clientApi.post("/auth/signup", payload);
    localStorage.setItem(CLIENT_TOKEN_KEY, data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(CLIENT_TOKEN_KEY);
    setUser(null);
  };

  const refreshUser = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, completeSetup, verifyTwoFactor, resendTwoFactor, googleComplete, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
